import React, { useEffect, useRef, useState } from 'react';
import {
    Button, Chip, CssBaseline, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Divider, Drawer, IconButton, LinearProgress, linearProgressClasses, ListItemIcon, Menu, MenuItem, Snackbar,
    TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { Box } from '@mui/system';
import { DataGrid, GridCloseIcon } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BackupIcon from '@mui/icons-material/Backup';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import HistoryIcon from '@mui/icons-material/History';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import './App.css';
import buildColumns from './columns';
import DisboxFileManager, { FILE_DELIMITER, fileKeyOf } from "./disbox-file-manager";
import { formatSize, getAvailableFileName, getMimeType, isPreviewable, pickLocationAsWritable, pickDirectoryLocation } from "./file-utils.js";
import PathParts from './PathParts';
import SearchBar from './SearchBar';
import { SEARCH_TYPES, searchEntries } from './search';
import CloseIcon from '@mui/icons-material/Close';
import Sidebar, { SIDEBAR_WIDTH } from './Sidebar';
import UploadPanel from './UploadPanel.js';
import PreviewDialog from './PreviewDialog';
import ShareDialog from './ShareDialog';
import TimeMachineDialog from './TimeMachineDialog';
import SettingsDialog from './SettingsDialog';
import SharedFilesDialog from './SharedFilesDialog';
import ContinueWatching from './ContinueWatching';
import { uploadToGofile } from './gofile-share.js';
import { blobFromAttachmentUrls } from './disbox-file-manager.js';
import { clearLocalSession, fetchAccountSettings, fileUrl, getAccount, hasValidSession, onAuthFailure, refreshSessionIfNeeded, wipeLocalData } from './api.js';
import { getContinueWatching, loadProgress, removeProgress } from './watch-progress';
import { canUseAnime } from './anime-library';
import { canStreamToDisk, collectZipEntries, downloadAsZip, totalZipBytes } from './zip-download';
import { onSharedFiles, takeSharedFiles } from './share-target';

// Dipakai bareng oleh tema gelap & terang: sudut lebih membulat + tombol
// tanpa huruf besar semua (bukan gaya lama "UPLOAD FILE") - kesan lebih
// modern & rapi, konsisten dengan sidebar & toolbar ikon yang baru.
const baseThemeOptions = {
    shape: { borderRadius: 10 },
    typography: {
        button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
        MuiButton: { styleOverrides: { root: { borderRadius: 10 } } },
        MuiListItemButton: { styleOverrides: { root: { borderRadius: 10 } } },
        MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    },
};

// Scrollbar ala Google Drive: tanpa tombol panah, track transparan, batang
// pil 8px (lebar 16px dikurangi border transparan 4px kiri-kanan) yang
// menggelap saat disorot/ditarik. Chrome/Edge memakai ::-webkit-scrollbar;
// scrollbar-color hanya untuk Firefox, karena di Chrome baru properti itu
// menimpa (mematikan) gaya ::-webkit-scrollbar.
function scrollbarStyles(mode) {
    const thumb = mode === 'dark' ? 'rgba(255,255,255,.24)' : 'rgba(0,0,0,.2)';
    const thumbHover = mode === 'dark' ? 'rgba(255,255,255,.4)' : 'rgba(0,0,0,.4)';
    return {
        '*::-webkit-scrollbar': { width: 16, height: 16 },
        '*::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
        '*::-webkit-scrollbar-track, *::-webkit-scrollbar-corner': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
            backgroundColor: thumb,
            backgroundClip: 'padding-box',
            border: '4px solid transparent',
            borderRadius: 8,
            minHeight: 40,
        },
        '*::-webkit-scrollbar-thumb:hover, *::-webkit-scrollbar-thumb:active': { backgroundColor: thumbHover },
        '@supports (-moz-appearance: none)': {
            '*': { scrollbarWidth: 'thin', scrollbarColor: `${thumb} transparent` },
        },
    };
}

const darkTheme = createTheme({
    ...baseThemeOptions,
    palette: {
        mode: 'dark',
        primary: { main: '#3ea6ff' },
    },
    components: {
        ...baseThemeOptions.components,
        MuiCssBaseline: { styleOverrides: scrollbarStyles('dark') },
    },
});

const lightTheme = createTheme({
    ...baseThemeOptions,
    palette: {
        mode: 'light',
        primary: { main: '#1a73e8' },
        background: { default: '#f7f8fa' },
    },
    components: {
        ...baseThemeOptions.components,
        MuiCssBaseline: { styleOverrides: scrollbarStyles('light') },
    },
});

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'light' ? '#1a90ff' : '#308fe8',
    },
}));

const VIEWS = ['trash', 'starred', 'recent'];

function parentPathOf(path) {
    if (!path) return '';
    const index = path.lastIndexOf(FILE_DELIMITER);
    return index <= 0 ? '' : path.slice(0, index);
}

// driveMode: dibuka lewat halaman /web/drive (bisa dari internet). Login,
// logout dan sesi habis kembali ke /drive, menu Anime disembunyikan, dan
// logout ikut menghapus data file yang tersimpan di browser.
function App({ driveMode = false }) {
    const loginRoute = driveMode ? '/drive' : '/setup';
    const navigate = useNavigate();
    const location = useLocation();
    // Menu "Baru" (New Folder/Upload File/Upload Folder) dan menu "Lainnya"
    // di toolbar - dipakai lewat MUI Menu.
    const [newMenuAnchor, setNewMenuAnchor] = useState(null);
    const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
    const [fileManager, setFileManager] = useState(null);
    const [rows, setRows] = useState([]);
    const [theme, setTheme] = useState(true);
    // Folder yang sedang dibuka ("" = root). null = sedang menampilkan daftar
    // "datar" (hasil pencarian, Berbintang, Terbaru, Trash).
    const [path, setPath] = useState(null);
    // 'files' | 'trash' | 'starred' | 'recent' | 'search'
    const [view, setView] = useState('files');
    const viewingTrash = view === 'trash';
    const flatView = view === 'starred' || view === 'recent';
    // Baris yang dicentang lewat checkbox DataGrid.
    const [selectionModel, setSelectionModel] = useState([]);
    // Teks di kolom Cari, dan halaman hasil pencarian yang sedang dibuka
    // ({ q, type, within, total, counts }, dari URL ?q=...&type=...&in=...).
    const [searchValue, setSearchValue] = useState("");
    const [searchInfo, setSearchInfo] = useState(null);
    // Urutan tabel: hasil pencarian awalnya urut paling cocok (tanpa sort kolom).
    const [folderSort, setFolderSort] = useState([{ field: 'name', sort: 'asc' }]);
    const [searchSort, setSearchSort] = useState([]);
    // Item yang dipilih otomatis setelah folder tujuan terbuka (dari saran pencarian).
    const pendingSelectRef = useRef(null);
    const [currentAction, setCurrentAction] = useState("");
    const [showProgress, setShowProgress] = useState(false);
    const [progressValue, setProgressValue] = useState(-1);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);
    const [syncStatus, setSyncStatus] = useState(null);
    // Hapus otomatis Trash akun ini (hari), 0 = manual. Diatur di Setelan.
    const [trashRetentionDays, setTrashRetentionDays] = useState(0);
    // Selama ganti nama di tabel, baris tidak bisa diseret: kalau bisa, blok
    // teks pakai mouse di kotak nama malah menyeret baris (pindah file).
    const [editingName, setEditingName] = useState(false);

    // Upload panel (gaya Google Drive), bisa dijeda & dilanjutkan.
    const [uploadItems, setUploadItems] = useState([]);
    const uploadJobsRef = useRef({});
    const uploadCancelRef = useRef(false);
    const uploadIdCounterRef = useRef(0);
    const uploadNotifiedRef = useRef(false);

    // Enkripsi file baru: dialog passphrase (kunci disimpan di perangkat ini)
    const [encDialogOpen, setEncDialogOpen] = useState(false);
    const [encPassphrase, setEncPassphrase] = useState("");
    const [encBusy, setEncBusy] = useState(false);
    const [encError, setEncError] = useState("");
    const [encEnabled, setEncEnabled] = useState(false);

    // Pindah file/folder: dialog "Pindahkan ke..." (dari toolbar untuk item
    // yang dicentang, atau dari menu titik tiga untuk satu item) dan drag & drop.
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [moveTarget, setMoveTarget] = useState("");
    const [moveItems, setMoveItems] = useState([]);
    const draggedIdsRef = useRef([]);
    const [dropTargetId, setDropTargetId] = useState(null);
    const [uploadDoneMessage, setUploadDoneMessage] = useState("");

    // Layar HP (< breakpoint 'sm' MUI, 600px): sidebar jadi Drawer yang
    // dibuka lewat tombol hamburger, tabel file pakai kolom yang dipangkas.
    const isMobile = useMediaQuery(darkTheme.breakpoints.down('sm'));
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // Rename Selected (bulk): cari & ganti teks di nama semua item yang dicentang.
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameFind, setRenameFind] = useState("");
    const [renameReplace, setRenameReplace] = useState("");
    // Ganti nama satu item (dari menu titik tiga).
    const [renameTarget, setRenameTarget] = useState(null);
    const [renameValue, setRenameValue] = useState("");

    // Preview, share, riwayat versi, file yang dibagikan dari aplikasi lain.
    const [previewRow, setPreviewRow] = useState(null);
    const [shareRow, setShareRow] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [sharedFiles, setSharedFiles] = useState(null);
    const [continueItems, setContinueItems] = useState([]);

    // Ref untuk dipakai handler yang dibuat sekali (listener file manager, breadcrumb).
    const fileManagerRef = useRef(null);
    fileManagerRef.current = fileManager;
    const viewRef = useRef(view);
    viewRef.current = view;
    const pathRef = useRef(path);
    pathRef.current = path;
    const lastFolderRef = useRef('');
    const pushedHashRef = useRef(null);

    // ===================== Navigasi (URL + tombol back HP) =====================
    // Folder yang dibuka disimpan di URL (?p=/Anime/Frieren, ?view=starred),
    // jadi tombol/gesture BACK di HP naik satu folder atau menutup preview,
    // bukan keluar dari app, dan folder bisa di-bookmark/di-refresh.

    const goToFolder = (target, { replace = false } = {}) => {
        navigate({ search: target ? `?p=${encodeURIComponent(target)}` : '' }, { replace });
    };
    const goToView = (target, { replace = false } = {}) => {
        navigate({ search: `?view=${target}` }, { replace });
    };
    const goToFolderRef = useRef(goToFolder);
    goToFolderRef.current = goToFolder;
    // Halaman hasil pencarian. within: hanya di folder ini ('' / null = semua).
    const goToSearch = (q, { type = 'all', within = null, replace = false } = {}) => {
        const params = new URLSearchParams({ q });
        if (type !== 'all') params.set('type', type);
        if (within) params.set('in', within);
        navigate({ search: `?${params.toString()}` }, { replace });
    };

    const pushHash = (hash) => {
        pushedHashRef.current = hash;
        navigate({ search: location.search, hash }, { replace: false });
    };
    const popHash = (hash, fallback) => {
        if (location.hash === hash && pushedHashRef.current === hash) {
            pushedHashRef.current = null;
            navigate(-1);
        } else {
            fallback();
        }
    };

    const showDirectory = (targetPath) => {
        setView('files');
        setPath(targetPath);
        const children = Object.values(fileManager.getChildren(targetPath));
        setRows(children);
        const pending = pendingSelectRef.current;
        pendingSelectRef.current = null;
        const picked = pending ? children.find((row) => row.path === pending) : null;
        setSelectionModel(picked ? [picked.id] : []);
        if (picked) {
            setTimeout(() => {
                const element = document.querySelector(`.MuiDataGrid-row[data-id="${picked.id}"]`);
                if (element) element.scrollIntoView({ block: 'center' });
            }, 150);
        }
    };

    // Hasil pencarian dibatasi supaya tabel tetap ringan (misal "ext:jpg" di
    // akun dengan ratusan ribu foto).
    const SEARCH_RESULT_LIMIT = 5000;
    const renderSearch = (fm, search) => {
        const { results, total, counts } = searchEntries(fm.searchIndex(), search.q, {
            type: search.type, within: search.within, limit: SEARCH_RESULT_LIMIT,
        });
        setView('search');
        setPath(null);
        setRows(results.map((entry) => fm.toRow(entry.node, entry.path)));
        setSearchInfo({ ...search, total, counts });
    };
    const searchInfoRef = useRef(searchInfo);
    searchInfoRef.current = searchInfo;

    const renderView = (fm, targetView) => {
        setView(targetView);
        setPath(null);
        if (targetView === 'trash') setRows(Object.values(fm.getTrashChildren()));
        else if (targetView === 'starred') setRows(fm.getStarredItems());
        else if (targetView === 'recent') setRows(fm.getRecentFiles());
        setSelectionModel([]);
    };

    // Tampilkan ulang tampilan yang sedang dibuka dari data terbaru (dipakai
    // setelah operasi, dan saat tree diganti hasil sinkronisasi/gabungan).
    const refreshCurrentView = () => {
        const fm = fileManagerRef.current;
        if (!fm) return;
        const currentView = viewRef.current;
        if (VIEWS.includes(currentView)) {
            renderView(fm, currentView);
            return;
        }
        if (currentView === 'search') {
            if (searchInfoRef.current) renderSearch(fm, searchInfoRef.current);
            return;
        }
        const currentPath = pathRef.current;
        if (currentPath === null) return;
        const node = currentPath === '' ? fm.fileTree : fm.getFile(currentPath, false);
        const target = node && node.type === 'directory' ? currentPath : '';
        setView('files');
        setPath(target);
        setRows(Object.values(fm.getChildren(target)));
    };
    const refreshRef = useRef(refreshCurrentView);
    refreshRef.current = refreshCurrentView;

    // URL -> tampilan
    useEffect(() => {
        if (!fileManager) return;
        const params = new URLSearchParams(location.search);
        const q = params.get('q');
        if (q !== null && q.trim()) {
            const type = SEARCH_TYPES.some((item) => item.id === params.get('type')) ? params.get('type') : 'all';
            renderSearch(fileManager, { q, type, within: params.get('in') || null });
            setSearchValue(q);
            setSearchSort([]);
            setSelectionModel([]);
            return;
        }
        // Keluar dari hasil pencarian (Back, menu samping, dll): kolom Cari dikosongkan.
        if (viewRef.current === 'search') setSearchValue('');
        setSearchInfo(null);
        const targetView = params.get('view');
        if (VIEWS.includes(targetView)) {
            renderView(fileManager, targetView);
            return;
        }
        const target = params.get('p') || '';
        const node = target ? fileManager.getFile(target) : null;
        if (target && (!node || node.type !== 'directory')) {
            navigate({ search: '' }, { replace: true });
            return;
        }
        lastFolderRef.current = target;
        showDirectory(target);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, fileManager]);

    // Hash #preview / #menu: tombol back menutup preview / menu samping.
    useEffect(() => {
        if (location.hash !== '#preview') {
            setPreviewRow((current) => {
                if (current) setContinueItems(getContinueWatching());
                return null;
            });
        }
        if (location.hash !== '#menu') setMobileNavOpen(false);
        if (location.hash && location.hash !== pushedHashRef.current) {
            // Hash tersisa dari reload halaman: bersihkan.
            navigate({ search: location.search }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.hash]);

    const openPreview = (row) => {
        setPreviewRow(row);
        if (location.hash !== '#preview') pushHash('#preview');
    };
    const closePreview = () => popHash('#preview', () => {
        setPreviewRow(null);
        setContinueItems(getContinueWatching());
    });

    const openMobileNav = () => {
        setMobileNavOpen(true);
        pushHash('#menu');
    };
    const closeMobileNav = () => popHash('#menu', () => setMobileNavOpen(false));
    // Navigasi dari dalam menu samping di HP: entri #menu diganti (bukan
    // ditambah), supaya back berikutnya tidak membuka menu lagi.
    const navFromDrawer = (action) => {
        setMobileNavOpen(false);
        pushedHashRef.current = null;
        action({ replace: location.hash === '#menu' });
    };

    // ===================== Mulai =====================

    useEffect(() => {
        onAuthFailure(() => {
            clearLocalSession();
            navigate(`${loginRoute}?expired=1`);
        });
        if (!hasValidSession()) {
            navigate(`${loginRoute}?relogin=1`, { replace: true });
            return undefined;
        }
        let unsubscribe = null;
        let cancelled = false;
        (async () => {
            await refreshSessionIfNeeded().catch(() => {});
            const account = getAccount();
            const manager = await DisboxFileManager.create(account.webhookUrls);
            if (cancelled) return;
            manager.discordFileStorage.setUploadWebhookCount(account.uploadWebhooks);
            manager.discordFileStorage.setChunkSize(account.uploadChunkBytes);
            // Setelan webhook bisa diubah dari perangkat lain: ambil yang terbaru.
            fetchAccountSettings(account.userId).then((settings) => {
                manager.discordFileStorage.setWebhookUrls(settings.webhookUrls || []);
                manager.discordFileStorage.setUploadWebhookCount(settings.uploadWebhooksAll ? null : settings.uploadWebhooks);
                manager.discordFileStorage.setChunkSize(settings.uploadChunkBytes);
                setTrashRetentionDays(Number(settings.trashRetentionDays) || 0);
            }).catch(() => {});
            manager.purgeStalePendingUploads();
            unsubscribe = manager.subscribe((event) => {
                if (event.type === 'tree-replaced') {
                    refreshRef.current();
                    if (event.reason === 'merged' && event.summary) {
                        setUploadDoneMessage(`Perubahan dari perangkat lain digabungkan: ${event.summary}`);
                    }
                } else if (event.type === 'backup-status') {
                    setSyncStatus(event.status);
                }
            });
            // Sinkronisasi otomatis: perubahan dari perangkat lain ditarik,
            // perubahan di perangkat ini yang belum tersimpan digabungkan.
            try {
                const { error } = await manager.syncFromDiscordIfNewer();
                if (error) console.warn("[Disbox] Sinkronisasi otomatis dari Discord gagal:", error.message);
            } catch (e) {
                console.warn("[Disbox] Sinkronisasi otomatis dari Discord gagal:", e);
            }
            if (cancelled) return;
            setFileManager(manager);
            setEncEnabled(manager.isEncryptionEnabled());
            setSyncStatus(manager.isDirty() ? 'pending' : 'saved');

            // Upload yang belum selesai dari sesi sebelumnya (tab tertutup/koneksi putus).
            const pending = manager.getPendingUploads();
            if (pending.length > 0) {
                setUploadItems((prev) => [...prev, ...pending.map((record) => {
                    uploadIdCounterRef.current += 1;
                    const done = Object.keys(record.refs || {}).length;
                    return {
                        id: uploadIdCounterRef.current,
                        name: record.path.split(FILE_DELIMITER).pop(),
                        size: record.size,
                        progress: record.totalChunks ? Math.round((done / record.totalChunks) * 100) : 0,
                        status: 'paused',
                        needsFile: true,
                        resumable: true,
                        pendingId: record.id,
                        pendingPath: record.path,
                        pendingName: record.name,
                    };
                })]);
            }

            loadProgress(manager.userId).then(() => {
                if (!cancelled) setContinueItems(getContinueWatching());
            });
            const shared = takeSharedFiles();
            if (shared.length > 0) setSharedFiles(shared);
        })();
        const offShared = onSharedFiles((files) => setSharedFiles(files));
        return () => {
            cancelled = true;
            if (unsubscribe) unsubscribe();
            offShared();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (progressValue === 100 || progressValue === -1) {
            const hide = setTimeout(() => {
                setShowProgress(false);
            }, 1500);
            const reset = setTimeout(() => {
                setProgressValue(-1);
                setCurrentAction("");
            }, 1600);
            return () => {
                clearTimeout(hide);
                clearTimeout(reset);
            };
        }
        setShowProgress(true);
        return undefined;
    }, [progressValue]);

    const getRowById = (id) => {
        return rows.find(row => row.id === id);
    };

    const updateRowById = (id, row) => {
        setRows(rows.map((r) => (r.id === id ? row : r)));
    };

    const deleteRowById = (id) => {
        setRows(rows.filter((r) => r.id !== id));
    };

    const addRow = (row) => {
        setRows([...rows, row]);
    };

    const onProgress = (value, total) => {
        const percentage = Math.round((value / Math.max(total, 1)) * 100).toFixed(0);
        setProgressValue(Math.min(100, Number(percentage)));
    };

    // ===================== Upload (bisa dijeda & dilanjutkan) =====================

    const updateUploadItem = (id, patch) => {
        setUploadItems(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item));
    };

    // Menjalankan (atau MELANJUTKAN) upload satu item. Potongan yang sudah
    // terkirim tidak dikirim ulang (lihat DisboxFileManager.uploadFile).
    // Kalau koneksi putus, dicoba lagi otomatis sampai 3x (jeda 15/30/45
    // detik); setelah itu tombol "Lanjutkan" muncul di panel upload.
    const runUploadJob = async (itemId) => {
        const job = uploadJobsRef.current[itemId];
        const fm = fileManagerRef.current;
        if (!job || !fm) return;
        clearTimeout(job.retryTimer);
        const controller = new AbortController();
        job.controller = controller;
        updateUploadItem(itemId, { status: 'uploading', error: null, message: null, needsFile: false });
        try {
            await fm.uploadFile(job.filePath, job.file, (loaded, total) => {
                const percentage = total ? Math.round((loaded / total) * 100) : 0;
                updateUploadItem(itemId, { progress: Math.min(percentage, 100), loadedBytes: loaded });
            }, { signal: controller.signal });
            updateUploadItem(itemId, { status: 'done', progress: 100 });
            delete uploadJobsRef.current[itemId];
            if (viewRef.current === 'files' && pathRef.current === parentPathOf(job.filePath)) refreshRef.current();
        } catch (e) {
            if (e && e.name === 'AbortError') {
                updateUploadItem(itemId, { status: 'paused', resumable: true });
                return;
            }
            const message = String((e && e.message) || e);
            const permanent = /status 4\d\d/.test(message);
            if (!permanent && job.retries < 3) {
                job.retries += 1;
                const delay = 15 * job.retries;
                updateUploadItem(itemId, {
                    status: 'retrying',
                    resumable: true,
                    message: `Koneksi putus. Dilanjutkan otomatis dalam ${delay} detik (percobaan ${job.retries}/3)...`,
                });
                job.retryTimer = setTimeout(() => runUploadJob(itemId), delay * 1000);
                return;
            }
            updateUploadItem(itemId, { status: 'error', resumable: !permanent, error: message });
        }
    };

    // Menjalankan antrian upload untuk banyak file sekaligus, menampilkan progress
    // per-file di panel upload (mirip Google Drive), sambil tetap membuat folder
    // yang diperlukan berdasarkan relativePath masing-masing file.
    const runUploadQueue = async (filesWithPaths, basePath) => {
        if (!filesWithPaths || filesWithPaths.length === 0) {
            return;
        }
        uploadCancelRef.current = false;

        // Minta izin notifikasi browser sekali (dipanggil dari klik user)
        try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
                Notification.requestPermission();
            }
        } catch (e) {
            // tidak wajib
        }

        const items = filesWithPaths.map(({ file, relativePath }) => {
            uploadIdCounterRef.current += 1;
            return {
                id: uploadIdCounterRef.current,
                name: relativePath || file.name,
                size: file.size,
                progress: 0,
                status: 'queued',
            };
        });
        setUploadItems(prev => [...prev, ...items]);

        const createdDirs = new Set();
        for (let i = 0; i < filesWithPaths.length; i++) {
            if (uploadCancelRef.current) {
                updateUploadItem(items[i].id, { status: 'canceled' });
                continue;
            }
            const { file, relativePath } = filesWithPaths[i];
            const itemId = items[i].id;
            try {
                const pathParts = (relativePath || file.name).split('/');
                const fileName = pathParts.pop();

                let currentDir = (basePath === null ? "" : basePath);
                for (const part of pathParts) {
                    const dirPath = `${currentDir}${FILE_DELIMITER}${part}`;
                    if (!createdDirs.has(dirPath)) {
                        const existing = fileManager.getFile(dirPath);
                        if (!existing) {
                            await fileManager.createDirectory(dirPath);
                        }
                        createdDirs.add(dirPath);
                    }
                    currentDir = dirPath;
                }

                const availableName = pathParts.length === 0
                    ? await getAvailableFileName(fileManager, currentDir, fileName)
                    : fileName;
                uploadJobsRef.current[itemId] = { file, filePath: `${currentDir}${FILE_DELIMITER}${availableName}`, retries: 0 };
                await runUploadJob(itemId);
            } catch (e) {
                updateUploadItem(itemId, { status: 'error', error: String(e && e.message ? e.message : e) });
            }
        }
        refreshRef.current();
    };

    const onPauseUpload = (itemId) => {
        const job = uploadJobsRef.current[itemId];
        if (!job) return;
        clearTimeout(job.retryTimer);
        if (job.controller) job.controller.abort();
    };

    const onResumeUpload = (itemId) => {
        const job = uploadJobsRef.current[itemId];
        if (!job) return;
        job.retries = 0;
        runUploadJob(itemId);
    };

    const onDiscardUpload = (itemId) => {
        const item = uploadItems.find((i) => i.id === itemId);
        const job = uploadJobsRef.current[itemId];
        if (!window.confirm('Buang upload ini? Bagian yang sudah terkirim ikut dihapus dari Discord.')) return;
        if (job) {
            clearTimeout(job.retryTimer);
            if (job.controller) job.controller.abort();
            fileManager.discardPendingUpload(`${job.filePath}|${job.file.size}|${job.file.lastModified || 0}`);
            delete uploadJobsRef.current[itemId];
        } else if (item && item.pendingId) {
            fileManager.discardPendingUpload(item.pendingId);
        }
        setUploadItems((prev) => prev.filter((i) => i.id !== itemId));
        refreshRef.current();
    };

    // Upload tertunda dari sesi sebelumnya: browser tidak mengingat file
    // aslinya, jadi file yang sama perlu dipilih lagi.
    const onPickFileForPending = (itemId, file) => {
        const item = uploadItems.find((i) => i.id === itemId);
        if (!item || !item.pendingId) return;
        if (file.size !== item.size || (item.pendingName && file.name !== item.pendingName)) {
            alert(`File yang dipilih berbeda (harus "${item.pendingName || item.name}", ${formatSize(item.size)}).`);
            return;
        }
        const record = fileManager.rekeyPendingUpload(item.pendingId, file);
        if (!record) {
            alert('Catatan upload ini sudah tidak ada. Upload ulang file-nya seperti biasa.');
            return;
        }
        uploadJobsRef.current[itemId] = { file, filePath: record.path, retries: 0 };
        updateUploadItem(itemId, { pendingId: null, needsFile: false });
        runUploadJob(itemId);
    };

    // Koneksi internet kembali: lanjutkan otomatis upload yang gagal/menunggu.
    useEffect(() => {
        const onOnline = () => {
            for (const [itemId, job] of Object.entries(uploadJobsRef.current)) {
                const item = uploadItems.find((i) => i.id === Number(itemId));
                if (item && (item.status === 'error' || item.status === 'retrying') && item.resumable) {
                    job.retries = 0;
                    runUploadJob(Number(itemId));
                }
            }
        };
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadItems]);

    // Saat SEMUA item di panel upload sudah selesai: beri notifikasi, dan kalau
    // semuanya sukses panel ditutup otomatis.
    useEffect(() => {
        if (uploadItems.length === 0) {
            uploadNotifiedRef.current = false;
            return undefined;
        }
        const allSettled = uploadItems.every(i => ['done', 'error', 'canceled', 'paused'].includes(i.status));
        if (!allSettled) {
            uploadNotifiedRef.current = false;
            return undefined;
        }
        if (uploadNotifiedRef.current) return undefined;
        uploadNotifiedRef.current = true;

        const doneCount = uploadItems.filter(i => i.status === 'done').length;
        const otherCount = uploadItems.length - doneCount;
        if (doneCount === 0) return undefined;

        const message = otherCount > 0
            ? `${doneCount} dari ${uploadItems.length} file terupload, ${otherCount} gagal/dijeda`
            : `Upload selesai: ${doneCount} file berhasil diupload`;
        setUploadDoneMessage(message);

        try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
                new Notification('Disbox', { body: message });
            }
        } catch (e) {
            // notifikasi browser tidak wajib
        }

        if (otherCount === 0) {
            const timer = setTimeout(() => setUploadItems([]), 1500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [uploadItems]);

    const onCancelAllUploads = () => {
        uploadCancelRef.current = true;
        for (const job of Object.values(uploadJobsRef.current)) {
            clearTimeout(job.retryTimer);
            if (job.controller) job.controller.abort();
        }
    };

    const onCloseUploadPanel = () => {
        setUploadItems((prev) => prev.filter((item) => item.status === 'paused' || (item.status === 'error' && item.resumable)));
    };

    // ===================== Navigasi tampilan =====================

    const onNavHome = () => goToFolder('');
    const onNavTrash = () => goToView('trash');
    const onNavStarred = () => goToView('starred');
    const onNavRecent = () => goToView('recent');

    const onEncryptionClick = () => {
        setEncPassphrase("");
        setEncError("");
        setEncDialogOpen(true);
    };

    const onLogoutClick = async () => {
        if (!window.confirm("Logout dari Disbox? File kamu tetap aman di Discord, tapi akun perlu login lagi di perangkat ini.")) {
            return;
        }
        if (!driveMode) {
            clearLocalSession();
            navigate("/setup");
            return;
        }
        // Simpan dulu perubahan yang belum terkirim, karena data di browser ikut dihapus.
        if (fileManager && fileManager.isDirty()) {
            await fileManager.indexBackup.flushNow().catch(() => {});
            if (fileManager.isDirty() && !window.confirm("Ada perubahan daftar file yang belum tersimpan ke Discord (bot tidak bisa dihubungi). Tetap logout dan buang perubahan itu?")) {
                return;
            }
        }
        await wipeLocalData();
        navigate(loginRoute, { replace: true });
    };

    const onEnableEncryption = async () => {
        setEncBusy(true);
        setEncError("");
        try {
            await fileManager.enableEncryption(encPassphrase);
            setEncEnabled(true);
            setEncDialogOpen(false);
            setEncPassphrase("");
            setUploadDoneMessage("Enkripsi aktif: file yang diupload mulai sekarang dienkripsi");
        } catch (e) {
            setEncError(String((e && e.message) || e));
        } finally {
            setEncBusy(false);
        }
    };

    const onDisableEncryption = () => {
        if (!window.confirm("Matikan enkripsi di perangkat ini? Kunci dihapus dari browser ini. File baru tidak dienkripsi lagi, dan file yang sudah terenkripsi tidak bisa dibuka di sini sampai kamu memasukkan passphrase lagi. Datanya sendiri tidak hilang.")) {
            return;
        }
        fileManager.disableEncryption();
        setEncEnabled(false);
        setEncDialogOpen(false);
    };

    // ===================== Pindah =====================

    // Memindahkan beberapa baris (file/folder) ke folder `targetPath` ("" = root).
    const moveRowsTo = async (rowsToMove, targetPath) => {
        if (currentAction || !rowsToMove || rowsToMove.length === 0) {
            return;
        }
        const failed = [];
        let movedCount = 0;
        for (const row of rowsToMove) {
            try {
                await fileManager.moveFile(row.path, targetPath);
                movedCount++;
            } catch (e) {
                failed.push(`${row.name}: ${e.message || e}`);
            }
        }
        refreshCurrentView();
        setSelectionModel([]);
        if (movedCount > 0) {
            const targetLabel = targetPath === "" ? "root" : targetPath.split(FILE_DELIMITER).pop();
            setUploadDoneMessage(`${movedCount} item dipindah ke "${targetLabel}"`);
        }
        if (failed.length > 0) {
            alert(`Sebagian gagal dipindah:\n${failed.join("\n")}`);
        }
    };

    // Folder tujuan yang valid untuk dialog "Pindahkan ke...": bukan folder
    // yang sedang dipindah (atau isinya), dan bukan folder asalnya.
    const getMoveDestinations = (items = moveItems) => {
        if (!fileManager) return [];
        return fileManager.getAllDirectoryPaths().filter((dirPath) => {
            if (items.length > 0 && items.every((row) => parentPathOf(row.path) === dirPath)) return false;
            return !items.some((row) => dirPath === row.path || dirPath.startsWith(row.path + FILE_DELIMITER));
        });
    };

    const openMoveDialog = (items) => {
        if (currentAction || items.length === 0) return;
        const destinations = getMoveDestinations(items);
        if (destinations.length === 0) {
            alert("Belum ada folder tujuan. Buat folder baru dulu (tombol Baru > Folder Baru).");
            return;
        }
        const parentPath = path ? parentPathOf(path) : null;
        setMoveItems(items);
        setMoveTarget(destinations.includes(parentPath) ? parentPath : destinations[0]);
        setMoveDialogOpen(true);
    };

    const onMoveSelectedClick = () => {
        openMoveDialog(selectionModel.map((id) => getRowById(id)).filter(Boolean));
    };

    const onConfirmMove = async () => {
        setMoveDialogOpen(false);
        await moveRowsTo(moveItems, moveTarget);
        setMoveItems([]);
    };

    // Drag & drop antar baris (desktop): baris (atau semua baris yang
    // dicentang) dijatuhkan ke baris folder.
    const MOVE_MIME = 'application/x-disbox-move';
    useEffect(() => { setEditingName(false); }, [path, view]);
    const rowIdOf = (e) => e.currentTarget.getAttribute('data-id');
    const findRowByDomId = (domId) => rows.find((r) => String(r.id) === String(domId));

    const onRowDragStart = (e) => {
        if (currentAction || path === null || viewingTrash) {
            e.preventDefault();
            return;
        }
        // Tarikan dari dalam kotak edit nama = seleksi teks, bukan pindah file.
        if (e.target.closest('.MuiDataGrid-cell--editing, input, textarea, [contenteditable="true"]')) {
            e.preventDefault();
            return;
        }
        const dragged = findRowByDomId(rowIdOf(e));
        if (!dragged) {
            e.preventDefault();
            return;
        }
        const selected = selectionModel.map((id) => getRowById(id)).filter(Boolean);
        const group = selected.some((r) => r.id === dragged.id) ? selected : [dragged];
        draggedIdsRef.current = group.map((r) => r.id);
        e.dataTransfer.setData(MOVE_MIME, String(dragged.id));
        e.dataTransfer.effectAllowed = 'move';
    };

    const isValidDropTarget = (targetRow) => {
        if (!targetRow || targetRow.type !== 'directory') return false;
        return draggedIdsRef.current.length > 0
            && !draggedIdsRef.current.some((id) => id === targetRow.id);
    };

    const onRowDragOver = (e) => {
        if (!e.dataTransfer.types.includes(MOVE_MIME)) return; // drag file dari luar: biar ditangani upload
        e.stopPropagation();
        const targetRow = findRowByDomId(rowIdOf(e));
        if (isValidDropTarget(targetRow)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (dropTargetId !== targetRow.id) setDropTargetId(targetRow.id);
        }
    };

    const onRowDragLeave = (e) => {
        if (!e.dataTransfer.types.includes(MOVE_MIME)) return;
        const targetRow = findRowByDomId(rowIdOf(e));
        if (targetRow && targetRow.id === dropTargetId && !e.currentTarget.contains(e.relatedTarget)) {
            setDropTargetId(null);
        }
    };

    const onRowDrop = async (e) => {
        if (!e.dataTransfer.types.includes(MOVE_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        const targetRow = findRowByDomId(rowIdOf(e));
        const ids = draggedIdsRef.current;
        draggedIdsRef.current = [];
        setDropTargetId(null);
        if (!targetRow || targetRow.type !== 'directory') {
            return;
        }
        const toMove = ids.map((id) => getRowById(id)).filter((r) => r && r.id !== targetRow.id);
        await moveRowsTo(toMove, targetRow.path);
    };

    // Drop ke breadcrumb (Storage > Folder A > ...): pindahkan ke folder itu.
    const onBreadcrumbDropRef = useRef(null);
    onBreadcrumbDropRef.current = async (targetPath) => {
        const ids = draggedIdsRef.current;
        draggedIdsRef.current = [];
        setDropTargetId(null);
        const toMove = ids.map((id) => getRowById(id)).filter(Boolean);
        await moveRowsTo(toMove, targetPath);
    };

    // Tombol "Move Up": keluarkan item terpilih ke folder induk.
    const onMoveUpClick = async () => {
        if (currentAction || path === null || path === "" || selectionModel.length === 0) {
            return;
        }
        const selectedRows = selectionModel.map((id) => getRowById(id)).filter(Boolean);
        await moveRowsTo(selectedRows, parentPathOf(path));
    };

    const onRowDragEnd = () => {
        draggedIdsRef.current = [];
        setDropTargetId(null);
    };

    // ===================== Rename =====================

    const onCellEditCommit = async (params) => {
        if (params.field !== "name" || viewingTrash) {
            return;
        }
        const row = getRowById(params.id);
        const newValue = params.value;
        if (!row || row.name === newValue) {
            return;
        }
        if (newValue.includes(FILE_DELIMITER)) {
            alert(`File name cannot contain "${FILE_DELIMITER}".`);
            updateRowById(params.id, row);
            return;
        }
        try {
            const changedFile = await fileManager.renameFile(row.path, newValue);
            updateRowById(params.id, changedFile && changedFile.type === 'directory' ? { ...changedFile, size: row.size } : changedFile);
        } catch (e) {
            alert(`Failed to rename file: ${e}`);
            updateRowById(params.id, row);
        }
    };

    const onRenameClick = (params) => {
        setRenameTarget(params.row);
        setRenameValue(params.row.name);
    };

    const onConfirmRename = async () => {
        const target = renameTarget;
        const newName = renameValue.trim();
        setRenameTarget(null);
        if (!target || !newName || newName === target.name) return;
        try {
            await fileManager.renameFile(target.path, newName);
            refreshCurrentView();
        } catch (e) {
            alert(`Gagal mengganti nama: ${e.message || e}`);
        }
    };

    const onToggleStar = async (params) => {
        try {
            const starred = await fileManager.toggleStar(params.row.path);
            refreshCurrentView();
            setUploadDoneMessage(starred ? `"${params.row.name}" diberi bintang` : `Bintang "${params.row.name}" dihapus`);
        } catch (e) {
            alert(`Gagal: ${e.message || e}`);
        }
    };

    // ===================== Buka folder / preview =====================

    const openRow = (row) => {
        if (row.type === "directory") {
            goToFolder(row.path);
        } else if (isPreviewable(row.name)) {
            openPreview(row);
        }
    };

    const onCellDoubleClick = async (params) => {
        if (params.field === "name" || viewingTrash || isMobile) {
            return;
        }
        openRow(params.row);
    };

    // Di HP cukup SEKALI ketuk untuk membuka folder/preview (seperti aplikasi
    // Google Drive) - ketuk dua kali di layar sentuh tidak bisa diandalkan.
    const onCellClick = (params) => {
        if (!isMobile || viewingTrash || currentAction) return;
        if (params.field === '__check__' || params.field === 'actions') return;
        openRow(params.row);
    };

    const onPreviewFileClick = (params) => {
        if (currentAction) return;
        openPreview(params.row);
    };

    // ===================== Hapus & Trash =====================

    const onDeleteFileClick = async (params) => {
        if (currentAction) {
            return;
        }
        const isDirectory = params.row.type === "directory";
        const confirmMessage = isDirectory
            ? `Pindahkan folder "${params.row.name}" beserta isinya ke Trash? Bisa dikembalikan lagi lewat Trash.`
            : `Pindahkan "${params.row.name}" ke Trash? Bisa dikembalikan lagi lewat Trash.`;
        if (!window.confirm(confirmMessage)) {
            return;
        }
        try {
            setCurrentAction(`Deleting ${params.row.name}`);
            await fileManager.deleteFile(params.row.path, onProgress);
            deleteRowById(params.row.id);
        } catch (e) {
            alert(`Failed to delete file: ${e}`);
        }
    };

    // Hapus semua baris yang dicentang sekaligus (satu per satu, bukan
    // paralel, supaya progress "X dari Y" akurat).
    const onBulkDeleteClick = async () => {
        if (currentAction || selectionModel.length === 0) {
            return;
        }
        const selectedRows = selectionModel.map((id) => getRowById(id)).filter(Boolean);
        if (selectedRows.length === 0) {
            return;
        }
        const hasDirectory = selectedRows.some((row) => row.type === "directory");
        const confirmMessage = hasDirectory
            ? `Pindahkan ${selectedRows.length} item terpilih (termasuk folder beserta seluruh isinya) ke Trash?`
            : `Pindahkan ${selectedRows.length} file terpilih ke Trash?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }
        const failed = [];
        for (let i = 0; i < selectedRows.length; i++) {
            const row = selectedRows[i];
            try {
                setCurrentAction(`Deleting ${row.name} (${i + 1}/${selectedRows.length})`);
                await fileManager.deleteFile(row.path, onProgress);
            } catch (e) {
                failed.push(row.name);
            }
        }
        refreshCurrentView();
        setProgressValue(100);
        if (failed.length > 0) {
            alert(`Gagal menghapus ${failed.length} item: ${failed.join(", ")}`);
        }
    };

    const onRestoreClick = async (params) => {
        if (currentAction) return;
        try {
            setCurrentAction(`Memulihkan ${params.row.name}`);
            await fileManager.restoreFile(params.row.path);
            refreshCurrentView();
        } catch (e) {
            alert(`Gagal memulihkan: ${e.message || e}`);
        } finally {
            setCurrentAction("");
        }
    };

    const onPermanentDeleteClick = async (params) => {
        if (currentAction) return;
        const isDirectory = params.row.type === "directory";
        const confirmMessage = isDirectory
            ? `Hapus PERMANEN folder "${params.row.name}" beserta isinya? Tindakan ini TIDAK BISA dibatalkan.`
            : `Hapus PERMANEN "${params.row.name}"? Tindakan ini TIDAK BISA dibatalkan.`;
        if (!window.confirm(confirmMessage)) return;
        try {
            setCurrentAction(`Menghapus permanen ${params.row.name}`);
            await fileManager.permanentlyDeleteFile(params.row.path, onProgress);
            refreshCurrentView();
        } catch (e) {
            alert(`Gagal menghapus permanen: ${e.message || e}`);
        } finally {
            setProgressValue(100);
        }
    };

    const onBulkRestoreClick = async () => {
        if (currentAction || selectionModel.length === 0) return;
        const selectedRows = selectionModel.map((id) => getRowById(id)).filter(Boolean);
        const failed = [];
        for (let i = 0; i < selectedRows.length; i++) {
            const row = selectedRows[i];
            try {
                setCurrentAction(`Memulihkan ${row.name} (${i + 1}/${selectedRows.length})`);
                await fileManager.restoreFile(row.path);
            } catch (e) {
                failed.push(row.name);
            }
        }
        refreshCurrentView();
        setCurrentAction("");
        if (failed.length > 0) {
            alert(`Gagal memulihkan ${failed.length} item: ${failed.join(", ")}`);
        }
    };

    const onBulkPermanentDeleteClick = async () => {
        if (currentAction || selectionModel.length === 0) return;
        const selectedRows = selectionModel.map((id) => getRowById(id)).filter(Boolean);
        if (!window.confirm(`Hapus PERMANEN ${selectedRows.length} item terpilih? Tindakan ini TIDAK BISA dibatalkan.`)) {
            return;
        }
        const failed = [];
        for (let i = 0; i < selectedRows.length; i++) {
            const row = selectedRows[i];
            try {
                setCurrentAction(`Menghapus permanen ${row.name} (${i + 1}/${selectedRows.length})`);
                await fileManager.permanentlyDeleteFile(row.path, onProgress);
            } catch (e) {
                failed.push(row.name);
            }
        }
        refreshCurrentView();
        setProgressValue(100);
        if (failed.length > 0) {
            alert(`Gagal menghapus permanen ${failed.length} item: ${failed.join(", ")}`);
        }
    };

    const onEmptyTrashClick = async () => {
        if (currentAction || !fileManager || fileManager.isTrashEmpty()) return;
        if (!window.confirm("Kosongkan Trash? SEMUA item di dalamnya akan dihapus PERMANEN dan TIDAK BISA dikembalikan lagi.")) {
            return;
        }
        try {
            setCurrentAction("Mengosongkan Trash");
            await fileManager.emptyTrash((value, total) => onProgress(value, Math.max(1, total)));
            refreshCurrentView();
        } catch (e) {
            alert(`Gagal mengosongkan Trash: ${e.message || e}`);
        } finally {
            setProgressValue(100);
        }
    };

    // ===================== Rename Selected (bulk cari & ganti) =====================

    const onBulkRenameClick = () => {
        if (currentAction || selectionModel.length === 0) return;
        setRenameFind("");
        setRenameReplace("");
        setRenameDialogOpen(true);
    };

    const getBulkRenameMatches = () => {
        if (!renameFind) return [];
        const selectedRows = selectionModel.map((id) => getRowById(id)).filter(Boolean);
        return selectedRows.filter((row) => row.name.includes(renameFind));
    };

    const onConfirmBulkRename = async () => {
        const matches = getBulkRenameMatches();
        setRenameDialogOpen(false);
        if (matches.length === 0) return;
        const failed = [];
        let renamed = 0;
        for (const row of matches) {
            const newName = row.name.split(renameFind).join(renameReplace);
            if (!newName || newName === row.name) continue;
            try {
                await fileManager.renameFile(row.path, newName);
                renamed++;
            } catch (e) {
                failed.push(`${row.name}: ${e.message || e}`);
            }
        }
        refreshCurrentView();
        if (renamed > 0) {
            setUploadDoneMessage(`${renamed} item di-rename`);
        }
        if (failed.length > 0) {
            alert(`Sebagian gagal di-rename:\n${failed.join("\n")}`);
        }
    };

    // ===================== Upload (tombol, drag & drop) =====================

    const onUploadFileClick = async (params) => {
        const files = params.target.files;
        if (!files || files.length === 0) {
            return;
        }
        const filesWithPaths = Array.from(files).map((file) => ({ file, relativePath: file.name }));
        params.target.value = null;
        await runUploadQueue(filesWithPaths, path);
    };

    const onUploadFolderClick = async (params) => {
        const files = params.target.files;
        if (!files || files.length === 0) return;
        const filesWithPaths = Array.from(files).map((file) => ({ file, relativePath: file.webkitRelativePath || file.name }));
        params.target.value = null;
        await runUploadQueue(filesWithPaths, path);
    };

    // Membaca satu FileSystemEntry (file atau directory) secara rekursif menjadi
    // daftar { file, relativePath }.
    const readEntry = (entry, basePath = "") => {
        return new Promise((resolve, reject) => {
            if (entry.isFile) {
                entry.file((file) => {
                    resolve([{ file, relativePath: `${basePath}${entry.name}` }]);
                }, reject);
            } else if (entry.isDirectory) {
                const reader = entry.createReader();
                let allEntries = [];
                const readBatch = () => {
                    reader.readEntries(async (entries) => {
                        if (entries.length === 0) {
                            try {
                                const nested = await Promise.all(
                                    allEntries.map((e) => readEntry(e, `${basePath}${entry.name}/`))
                                );
                                resolve(nested.flat());
                            } catch (e) {
                                reject(e);
                            }
                            return;
                        }
                        allEntries = allEntries.concat(entries);
                        readBatch();
                    }, reject);
                };
                readBatch();
            } else {
                resolve([]);
            }
        });
    };

    const getFilesFromDataTransfer = async (dataTransfer) => {
        const items = dataTransfer.items;
        if (items && items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
            const entries = Array.from(items)
                .map((item) => item.webkitGetAsEntry())
                .filter(Boolean);
            const results = await Promise.all(entries.map((entry) => readEntry(entry)));
            return results.flat();
        }
        return Array.from(dataTransfer.files).map((file) => ({ file, relativePath: file.name }));
    };

    const onFilesDropped = async (dataTransfer) => {
        if (path === null) {
            return;
        }
        let filesWithPaths;
        try {
            filesWithPaths = await getFilesFromDataTransfer(dataTransfer);
        } catch (e) {
            alert(`Failed to read dropped files: ${e}`);
            return;
        }
        if (!filesWithPaths || filesWithPaths.length === 0) {
            return;
        }
        await runUploadQueue(filesWithPaths, path);
    };

    const onDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (currentAction || path === null) {
            return;
        }
        dragCounterRef.current += 1;
        if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
            setIsDragging(true);
        }
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0;
            setIsDragging(false);
        }
    };

    const onDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        if (!e.dataTransfer) {
            return;
        }
        await onFilesDropped(e.dataTransfer);
    };

    const onUploadShared = async (files, targetPath) => {
        setSharedFiles(null);
        try {
            if (targetPath && !fileManager.getFile(targetPath)) await fileManager.createDirectory(targetPath);
            goToFolder(targetPath);
            await runUploadQueue(files.map((file) => ({ file, relativePath: file.name })), targetPath);
        } catch (e) {
            alert(`Gagal menyiapkan upload: ${e.message || e}`);
        }
    };

    // ===================== Download =====================

    // Download satu file LANGSUNG dari bot (link dengan header "attachment"):
    // ditangani download manager browser, jalan di HP juga, tidak menampung
    // file di memori, dan bisa dilanjutkan oleh browser kalau putus.
    const downloadViaBot = (row) => {
        const link = document.createElement('a');
        link.href = fileUrl('stream', fileManager.userId, row.path, { dl: 1 });
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setUploadDoneMessage(`Download "${row.name}" dimulai`);
    };

    const onDownloadZip = async (rowsToZip) => {
        if (currentAction || rowsToZip.length === 0) return;
        const entries = collectZipEntries(fileManager, rowsToZip);
        const total = totalZipBytes(entries);
        if (!canStreamToDisk() && total > 1.5 * 1024 ** 3
            && !window.confirm(`ZIP ini ${formatSize(total)}. Di browser ini seluruh isinya ditampung di memori dulu sebelum tersimpan, bisa lambat/gagal untuk ukuran sebesar ini. Lanjutkan?`)) {
            return;
        }
        const zipName = rowsToZip.length === 1 ? `${rowsToZip[0].name}.zip` : `disbox-${new Date().toISOString().slice(0, 10)}.zip`;
        try {
            setCurrentAction(`Membuat ${zipName}`);
            await downloadAsZip(fileManager, rowsToZip, zipName, onProgress);
            setProgressValue(100);
        } catch (e) {
            setProgressValue(-1);
            if (!e || e.name !== 'AbortError') alert(`Gagal membuat ZIP: ${e.message || e}`);
        }
    };

    const onDownloadFileClick = async (params) => {
        if (currentAction) {
            return;
        }
        const row = params.row || params;
        try {
            if (row.type === "directory") {
                // Chrome/Edge desktop: folder disimpan apa adanya. Browser lain (HP): sebagai ZIP.
                if (typeof window.showDirectoryPicker !== 'function') {
                    await onDownloadZip([row]);
                    return;
                }
                const dirHandle = await pickDirectoryLocation();
                setCurrentAction(`Downloading Folder ${row.name}`);

                const downloadDirectoryRecursive = async (dirPath, currentHandle) => {
                    const children = fileManager.getChildren(dirPath);
                    for (const key in children) {
                        const child = children[key];
                        if (child.type === "directory") {
                            const newHandle = await currentHandle.getDirectoryHandle(child.name, { create: true });
                            await downloadDirectoryRecursive(child.path, newHandle);
                        } else {
                            const fileHandle = await currentHandle.getFileHandle(child.name, { create: true });
                            const writable = await fileHandle.createWritable();
                            await fileManager.downloadFile(child.path, writable, onProgress);
                        }
                    }
                };

                await downloadDirectoryRecursive(row.path, dirHandle);
                setProgressValue(100);
                return;
            }
            if (!row.enc) {
                downloadViaBot(row);
                return;
            }
            // File terenkripsi: didekripsi di browser ini (bot tidak memegang kuncinya).
            const writable = await pickLocationAsWritable(row.name);
            setCurrentAction(`Downloading ${row.name}`);
            await fileManager.downloadFile(row.path, writable, onProgress);
        } catch (e) {
            if (!e || e.name !== 'AbortError') alert(`Failed to download: ${e}`);
        }
    };

    // ===================== Share =====================

    const onShareFileClick = (params) => {
        if (currentAction || params.row.type === "directory") return;
        setShareRow(params.row);
    };

    // Upload ke GoFile.io (link publik permanen, file dikirim ke pihak ketiga).
    const shareViaGofile = async (row) => {
        if (currentAction) {
            return;
        }
        try {
            const fileName = row.name;
            const fileSize = row.size || 1;
            const mimeType = getMimeType(fileName);

            setCurrentAction(`Menyiapkan ${fileName} untuk di-share...`);
            const attachmentUrls = await fileManager.getAttachmentUrls(row.path);
            // File terenkripsi didekripsi dulu, karena penerima link tidak punya kuncinya.
            const decrypt = fileManager.getDecryptor(fileManager.getFile(row.path));

            // Fase 1 (0-50%): ambil & gabungkan semua chunk file dari Discord.
            const blob = await blobFromAttachmentUrls(attachmentUrls, mimeType, (loaded, total) => {
                onProgress(loaded, total * 2);
            }, fileSize, 6, decrypt);

            // Fase 2 (50-100%): upload file utuh itu ke GoFile.
            setCurrentAction(`Mengupload ${fileName} ke GoFile...`);
            const result = await uploadToGofile(blob, fileName, (loaded, total) => {
                onProgress(fileSize + loaded, fileSize + total);
            });
            setProgressValue(100);

            const shareUrl = result.downloadPage;
            let shared = false;
            if (navigator.share) {
                try {
                    await navigator.share({ title: fileName, url: shareUrl });
                    shared = true;
                } catch (e) {
                    shared = false;
                }
            }
            if (!shared) {
                try {
                    await navigator.clipboard.writeText(shareUrl);
                } catch { /* clipboard tidak tersedia di http biasa */ }
                window.prompt('File berhasil diupload ke GoFile. Link:', shareUrl);
            }
        } catch (e) {
            setProgressValue(-1);
            alert(`Gagal share file ke GoFile: ${e.message || e}`);
        }
    };

    // ===================== Index: backup, restore, sinkron =====================

    const onBackupIndexClick = () => {
        if (!fileManager) return;
        const json = fileManager.exportIndex();
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `disbox-index-backup-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const onRestoreIndexClick = async (params) => {
        const file = params.target.files[0];
        params.target.value = null;
        if (!file || !fileManager) return;
        if (!window.confirm("Ini akan menimpa daftar file yang sedang tampil dengan isi dari file backup. Lanjutkan?")) {
            return;
        }
        try {
            const text = await file.text();
            fileManager.importIndex(text);
            refreshCurrentView();
            setUploadDoneMessage("Index dari file backup sudah dipasang dan sedang disimpan ke Discord");
        } catch (e) {
            alert(`Gagal restore index: ${e}`);
        }
    };

    const onSyncNowClick = async () => {
        if (!fileManager) return;
        setUploadDoneMessage("Menyinkronkan dengan Discord...");
        try {
            const { applied, error } = await fileManager.syncFromDiscordIfNewer();
            if (error) throw error;
            refreshCurrentView();
            setUploadDoneMessage(applied ? "Daftar file diperbarui dari Discord" : "Sudah yang terbaru");
        } catch (e) {
            setUploadDoneMessage(`Sinkronisasi gagal: ${e.message || e}`);
        }
    };

    const onNewFolderClick = async () => {
        try {
            const folderName = await getAvailableFileName(fileManager, path, "New Folder");
            const folderPath = `${path}${FILE_DELIMITER}${folderName}`;

            await fileManager.createDirectory(folderPath);
            addRow(fileManager.toRow(fileManager.getFile(folderPath, false), folderPath));
        } catch (e) {
            alert(`Failed to create folder: ${e}`);
        }
    };

    // ===================== Pencarian =====================

    // Enter / "Lihat semua hasil": semua hasil di tabel. Kalau halaman hasil
    // sudah terbuka, filter jenis & folder yang dipilih tetap dipakai.
    const onSearchSubmit = (text) => {
        const keep = view === 'search' && searchInfo ? searchInfo : null;
        goToSearch(text.trim(), { type: keep ? keep.type : 'all', within: keep ? keep.within : null, replace: view === 'search' });
    };

    // Saran dipilih: folder dibuka, file yang bisa di-preview langsung diputar,
    // file lain ditunjukkan di foldernya (dipilih).
    const onSearchPick = (entry) => {
        setSearchValue('');
        if (entry.type === 'directory') {
            goToFolder(entry.path);
            return;
        }
        const row = fileManager.getFile(entry.path, false);
        if (row && isPreviewable(entry.name)) {
            openPreview(fileManager.toRow(row, entry.path));
            return;
        }
        onOpenLocation({ path: entry.path });
    };

    const onOpenLocation = (row) => {
        const index = row.path.lastIndexOf(FILE_DELIMITER);
        const parent = index <= 0 ? '' : row.path.slice(0, index);
        pendingSelectRef.current = row.path;
        if (view === 'files' && path === parent) {
            showDirectory(parent);
            return;
        }
        goToFolder(parent);
    };

    const closeSearch = () => {
        setSearchValue('');
        if (view === 'search') goToFolder(lastFolderRef.current);
    };

    // ===================== Lanjutkan menonton =====================

    const onOpenContinue = (item) => {
        let row = fileManager.getFile(item.path);
        if (!row || fileKeyOf(row) !== item.key) row = fileManager.findFileByKey(item.key);
        if (!row) {
            removeProgress(fileManager.userId, item.key);
            setContinueItems(getContinueWatching());
            alert('File ini sudah tidak ada (terhapus atau dipindah ke Trash).');
            return;
        }
        openPreview(row);
    };

    const onRemoveContinue = (item) => {
        removeProgress(fileManager.userId, item.key);
        setContinueItems(getContinueWatching());
    };

    const trashItemCount = fileManager ? Object.keys(fileManager.getTrashChildren()).length : 0;
    const storageUsed = fileManager ? fileManager.getTotalStorageUsed() : "0 Bytes";
    const selectedRows = selectionModel.map((id) => getRowById(id)).filter(Boolean);
    const showLocation = flatView || view === 'search';

    const columnHandlers = {
        onPreview: onPreviewFileClick,
        onShare: onShareFileClick,
        onDownload: onDownloadFileClick,
        onDownloadZip: (params) => onDownloadZip([params.row]),
        onDelete: onDeleteFileClick,
        onRestore: onRestoreClick,
        onPermanentDelete: onPermanentDeleteClick,
        onMove: (params) => openMoveDialog([params.row]),
        onRename: onRenameClick,
        onToggleStar,
        onOpenLocation: (params) => onOpenLocation(params.row),
    };

    const sidebarProps = {
        view,
        trashCount: trashItemCount,
        storageUsed,
        syncStatus,
        encEnabled,
        theme,
        setTheme,
        onLogout: onLogoutClick,
        onNavAnime: !driveMode && canUseAnime() ? () => navigate('/anime') : null,
    };

    const viewTitle = view === 'starred' ? '⭐ Berbintang' : view === 'recent' ? '🕘 Terbaru (upload terakhir)' : null;

    return (
        <div
            style={{ height: "100vh", position: "relative" }}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {isDragging && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(26, 144, 255, 0.15)",
                        border: "3px dashed #1a90ff",
                        borderRadius: "8px",
                        pointerEvents: "none",
                    }}
                >
                    <Typography variant="h5" sx={{ color: "#1a90ff", fontWeight: "bold", backgroundColor: "background.paper", padding: "1rem 2rem", borderRadius: "8px" }}>
                        Drop files here to upload
                    </Typography>
                </Box>
            )}
            <ThemeProvider theme={theme ? darkTheme : lightTheme}>
                <CssBaseline />
                {fileManager && (
                    <PreviewDialog
                        open={Boolean(previewRow)}
                        row={previewRow}
                        fileManager={fileManager}
                        isMobile={isMobile}
                        onClose={closePreview}
                        onNavigate={(next) => setPreviewRow(next)}
                        onDownload={(row) => onDownloadFileClick({ row })}
                    />
                )}
                {fileManager && (
                    <ShareDialog
                        open={Boolean(shareRow)}
                        row={shareRow}
                        fileManager={fileManager}
                        onClose={() => setShareRow(null)}
                        onGofile={shareViaGofile}
                    />
                )}
                {fileManager && (
                    <TimeMachineDialog
                        open={historyOpen}
                        fileManager={fileManager}
                        onClose={() => setHistoryOpen(false)}
                        onRestored={() => refreshCurrentView()}
                    />
                )}
                {fileManager && (
                    <SettingsDialog
                        open={settingsOpen}
                        fileManager={fileManager}
                        onClose={() => setSettingsOpen(false)}
                        onSaved={(settings) => setTrashRetentionDays(Number(settings.trashRetentionDays) || 0)}
                    />
                )}
                {fileManager && (
                    <SharedFilesDialog
                        open={Boolean(sharedFiles && sharedFiles.length > 0)}
                        files={sharedFiles}
                        fileManager={fileManager}
                        onClose={() => setSharedFiles(null)}
                        onUpload={onUploadShared}
                    />
                )}
                <Dialog open={encDialogOpen} onClose={() => { if (!encBusy) setEncDialogOpen(false); }} maxWidth="sm" fullWidth>
                    <DialogTitle>Enkripsi file baru {encEnabled ? "(aktif)" : "(mati)"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText style={{ marginBottom: "12px" }}>
                            {encEnabled
                                ? "File yang kamu upload dari web ini sudah dienkripsi sebelum dikirim ke Discord. File lama yang belum terenkripsi tetap bisa dibuka seperti biasa."
                                : "Isi passphrase untuk mengenkripsi file yang diupload MULAI SEKARANG. File lama tidak berubah dan tetap bisa dibuka."}
                        </DialogContentText>
                        {!encEnabled && (
                            <>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    margin="dense"
                                    type="password"
                                    // "new-password" mencegah password manager browser mengisi kolom ini
                                    // dan menaruh username tersimpan (mis. "admin") ke kolom Search.
                                    name="disbox-encryption-passphrase"
                                    autoComplete="new-password"
                                    label="Passphrase (minimal 8 karakter)"
                                    value={encPassphrase}
                                    onChange={(e) => setEncPassphrase(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter" && !encBusy) onEnableEncryption(); }}
                                    disabled={encBusy}
                                />
                                <DialogContentText color="error" style={{ marginTop: "8px" }}>
                                    Passphrase yang lupa TIDAK bisa dipulihkan: file terenkripsi jadi tidak bisa dibuka selamanya. Catat di tempat aman.
                                    Passphrase harus SAMA dengan ENCRYPTION_PASSPHRASE di bot (kalau bot dipakai untuk WebDAV).
                                </DialogContentText>
                                {encError && (
                                    <DialogContentText color="error" style={{ marginTop: "8px", fontWeight: "bold" }}>{encError}</DialogContentText>
                                )}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEncDialogOpen(false)} disabled={encBusy}>Tutup</Button>
                        {encEnabled
                            ? <Button onClick={onDisableEncryption} color="error">Matikan di perangkat ini</Button>
                            : <Button onClick={onEnableEncryption} variant="contained" disabled={encBusy || encPassphrase.length < 8}>{encBusy ? "Memproses..." : "Aktifkan"}</Button>}
                    </DialogActions>
                </Dialog>
                <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {moveItems.length === 1 ? `Pindahkan "${moveItems[0].name}"` : `Pindahkan ${moveItems.length} item`}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            select
                            fullWidth
                            margin="dense"
                            label="Folder tujuan"
                            value={moveTarget}
                            onChange={(e) => setMoveTarget(e.target.value)}
                            SelectProps={{ native: true }}
                            InputLabelProps={{ shrink: true }}
                        >
                            {getMoveDestinations().map((dirPath) => (
                                <option key={dirPath} value={dirPath}>
                                    {dirPath === "" ? "/ (Root)" : dirPath}
                                </option>
                            ))}
                        </TextField>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setMoveDialogOpen(false)}>Batal</Button>
                        <Button onClick={onConfirmMove} variant="contained">Pindahkan</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} maxWidth="sm" fullWidth>
                    <DialogTitle>Ganti nama</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            margin="dense"
                            label="Nama baru"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') onConfirmRename(); }}
                            onFocus={(e) => {
                                const dot = e.target.value.lastIndexOf('.');
                                if (dot > 0 && renameTarget && renameTarget.type !== 'directory') e.target.setSelectionRange(0, dot);
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRenameTarget(null)}>Batal</Button>
                        <Button onClick={onConfirmRename} variant="contained" disabled={!renameValue.trim() || renameValue.includes(FILE_DELIMITER)}>Simpan</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Rename {selectionModel.length} item terpilih</DialogTitle>
                    <DialogContent>
                        <DialogContentText style={{ marginBottom: "12px" }}>
                            Cari teks di nama file, ganti dengan teks lain. Cuma item terpilih yang namanya
                            mengandung teks "Cari" yang akan diubah, sisanya dilewati.
                        </DialogContentText>
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Cari"
                            value={renameFind}
                            onChange={(e) => setRenameFind(e.target.value)}
                            autoFocus
                        />
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Ganti dengan"
                            value={renameReplace}
                            onChange={(e) => setRenameReplace(e.target.value)}
                        />
                        <DialogContentText style={{ marginTop: "12px" }}>
                            {renameFind
                                ? `${getBulkRenameMatches().length} dari ${selectionModel.length} item terpilih akan diubah.`
                                : "Isi \"Cari\" dulu untuk melihat berapa item yang akan terdampak."}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRenameDialogOpen(false)}>Batal</Button>
                        <Button onClick={onConfirmBulkRename} variant="contained" disabled={!renameFind || getBulkRenameMatches().length === 0}>Rename</Button>
                    </DialogActions>
                </Dialog>
                <UploadPanel
                    items={uploadItems}
                    onCancelAll={onCancelAllUploads}
                    onClose={onCloseUploadPanel}
                    onPause={onPauseUpload}
                    onResume={onResumeUpload}
                    onDiscard={onDiscardUpload}
                    onPickFile={onPickFileForPending}
                />
                <Snackbar
                    message={uploadDoneMessage}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center', }}
                    open={uploadDoneMessage !== ""}
                    autoHideDuration={5000}
                    onClose={() => setUploadDoneMessage("")}
                ></Snackbar>
                <Snackbar
                    message=""
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right', }}
                    open={showProgress}
                >
                    <Box style={{ backgroundColor: "white", width: "min(500px, calc(100vw - 32px))", height: "60px" }}>
                        <Box sx={{ width: '100%', mr: 1, ml: 1, mt: 1, mb: -0.5 }}>
                            <Typography sx={{ color: "black" }} variant="body2" color="text.secondary" noWrap>{currentAction}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginTop: "4px" }}>
                            <Box sx={{ width: '100%', mr: 1, ml: 1 }}>
                                <BorderLinearProgress variant="determinate" value={Math.max(progressValue, 0)} />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                                <Typography sx={{ color: "black" }} variant="body2" color="text.secondary">{`${Math.max(progressValue, 0)}%`}</Typography>
                            </Box>
                            <IconButton size="small" aria-label="close" sx={{ color: "black" }} onClick={() => { setShowProgress(false) }}>
                                <GridCloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </Snackbar>
                <Box sx={{ display: 'flex', height: '100%' }}>
                    {isMobile ? (
                        <Drawer
                            variant="temporary"
                            open={mobileNavOpen}
                            onClose={closeMobileNav}
                            ModalProps={{ keepMounted: true }}
                            PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
                        >
                            <Sidebar
                                {...sidebarProps}
                                onNavHome={() => navFromDrawer((opts) => goToFolder('', opts))}
                                onNavStarred={() => navFromDrawer((opts) => goToView('starred', opts))}
                                onNavRecent={() => navFromDrawer((opts) => goToView('recent', opts))}
                                onNavTrash={() => navFromDrawer((opts) => goToView('trash', opts))}
                                onOpenHistory={() => { closeMobileNav(); setHistoryOpen(true); }}
                                onOpenSettings={() => { closeMobileNav(); setSettingsOpen(true); }}
                                onEncryptionClick={() => { closeMobileNav(); onEncryptionClick(); }}
                            />
                        </Drawer>
                    ) : (
                        <Sidebar
                            {...sidebarProps}
                            onNavHome={onNavHome}
                            onNavStarred={onNavStarred}
                            onNavRecent={onNavRecent}
                            onNavTrash={onNavTrash}
                            onOpenHistory={() => setHistoryOpen(true)}
                            onOpenSettings={() => setSettingsOpen(true)}
                            onEncryptionClick={onEncryptionClick}
                        />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <input id="uploadFile" type="file" multiple style={{ display: "none" }} onChange={onUploadFileClick}></input>
                        <input id="uploadFolder" type="file" webkitdirectory="true" directory="true" multiple style={{ display: "none" }} onChange={onUploadFolderClick}></input>
                        <input id="restoreIndex" type="file" accept="application/json" style={{ display: "none" }} onChange={onRestoreIndexClick}></input>

                        {isMobile && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <IconButton onClick={openMobileNav} edge="start" aria-label="Menu">
                                    <MenuIcon />
                                </IconButton>
                                <CloudQueueIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Disbox</Typography>
                            </Box>
                        )}

                        <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 2.5 }}>
                            <SearchBar fileManager={fileManager} version={rows} value={searchValue}
                                onChange={setSearchValue} onSubmit={onSearchSubmit} onPick={onSearchPick} onClear={closeSearch}
                                placeholder={isMobile ? 'Cari di Drive' : 'Cari di Drive (nama file, folder, atau ext:mkv)'} />
                        </Box>

                        <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {!viewingTrash && <>
                                <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={(e) => setNewMenuAnchor(e.currentTarget)} disabled={path === null}>
                                    Baru
                                </Button>
                                <Menu anchorEl={newMenuAnchor} open={!!newMenuAnchor} onClose={() => setNewMenuAnchor(null)}>
                                    <MenuItem disabled={currentAction !== ""} onClick={() => { setNewMenuAnchor(null); onNewFolderClick(); }}>
                                        <ListItemIcon><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
                                        Folder Baru
                                    </MenuItem>
                                    <MenuItem onClick={() => { setNewMenuAnchor(null); document.getElementById("uploadFile").click(); }}>
                                        <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
                                        Upload File
                                    </MenuItem>
                                    <MenuItem onClick={() => { setNewMenuAnchor(null); document.getElementById("uploadFolder").click(); }}>
                                        <ListItemIcon><DriveFolderUploadIcon fontSize="small" /></ListItemIcon>
                                        Upload Folder
                                    </MenuItem>
                                </Menu>

                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

                                <Tooltip title="Pindahkan item terpilih ke folder lain">
                                    <span>
                                        <IconButton onClick={onMoveSelectedClick} disabled={(currentAction !== "" || selectedRows.length === 0)}>
                                            <DriveFileMoveIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {!flatView && (
                                    <Tooltip title="Keluarkan item terpilih ke folder induk">
                                        <span>
                                            <IconButton onClick={onMoveUpClick} disabled={(currentAction !== "" || path === null || path === "" || selectionModel.length === 0)}>
                                                <ArrowUpwardIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                <Tooltip title="Rename Selected (cari & ganti teks di nama)">
                                    <span>
                                        <IconButton onClick={onBulkRenameClick} disabled={(currentAction !== "" || selectionModel.length === 0)}>
                                            <FindReplaceIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Download item terpilih sebagai ZIP">
                                    <span>
                                        <IconButton onClick={() => onDownloadZip(selectedRows)} disabled={(currentAction !== "" || selectedRows.length === 0)}>
                                            <FolderZipIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Hapus item terpilih (pindah ke Trash)">
                                    <span>
                                        <IconButton color="error" onClick={onBulkDeleteClick} disabled={(currentAction !== "" || selectionModel.length === 0)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {selectionModel.length > 0 && <Chip label={`${selectionModel.length} dipilih`} size="small" />}

                                {!isMobile && <Box sx={{ flex: 1 }} />}

                                <Tooltip title="Lainnya">
                                    <IconButton onClick={(e) => setMoreMenuAnchor(e.currentTarget)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </Tooltip>
                                <Menu anchorEl={moreMenuAnchor} open={!!moreMenuAnchor} onClose={() => setMoreMenuAnchor(null)}>
                                    <MenuItem onClick={() => { setMoreMenuAnchor(null); onSyncNowClick(); }}>
                                        <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
                                        Sinkronkan sekarang
                                    </MenuItem>
                                    <MenuItem onClick={() => { setMoreMenuAnchor(null); setHistoryOpen(true); }}>
                                        <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                                        Riwayat versi
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={() => { setMoreMenuAnchor(null); onBackupIndexClick(); }}>
                                        <ListItemIcon><BackupIcon fontSize="small" /></ListItemIcon>
                                        Backup Index (file JSON)
                                    </MenuItem>
                                    <MenuItem onClick={() => { setMoreMenuAnchor(null); document.getElementById("restoreIndex").click(); }}>
                                        <ListItemIcon><SettingsBackupRestoreIcon fontSize="small" /></ListItemIcon>
                                        Restore Index (file JSON)
                                    </MenuItem>
                                </Menu>
                            </>}

                            {viewingTrash && <>
                                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => goToFolder(lastFolderRef.current)} disabled={currentAction !== ""}>
                                    Kembali
                                </Button>

                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

                                <Tooltip title="Restore Selected">
                                    <span>
                                        <IconButton onClick={onBulkRestoreClick} disabled={(currentAction !== "" || selectionModel.length === 0)}>
                                            <RestoreFromTrashIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Delete Forever">
                                    <span>
                                        <IconButton color="error" onClick={onBulkPermanentDeleteClick} disabled={(currentAction !== "" || selectionModel.length === 0)}>
                                            <DeleteForeverIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {selectionModel.length > 0 && <Chip label={`${selectionModel.length} dipilih`} size="small" />}

                                {!isMobile && <Box sx={{ flex: 1 }} />}

                                <Button color="error" variant="outlined" startIcon={<DeleteSweepIcon />} onClick={onEmptyTrashClick} disabled={(currentAction !== "" || !fileManager || fileManager.isTrashEmpty())}>
                                    Kosongkan Trash
                                </Button>
                            </>}
                        </Box>

                        <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 1 }}>
                            {view === 'files' && (
                                <PathParts path={path} fileManager={fileManager} showDirectory={(target) => goToFolderRef.current(target)} onMoveDrop={(targetPath) => onBreadcrumbDropRef.current(targetPath)} />
                            )}
                            {viewingTrash && (
                                <Typography variant="body2" color="text.secondary">
                                    🗑 Trash: item di sini bisa di-Restore atau dihapus permanen.{' '}
                                    {trashRetentionDays > 0
                                        ? `Item yang sudah ${trashRetentionDays} hari di Trash dihapus permanen otomatis.`
                                        : 'Tidak dihapus otomatis (bisa diatur di Setelan).'}
                                </Typography>
                            )}
                            {viewTitle && (
                                <Typography variant="subtitle2" color="text.secondary">{viewTitle}</Typography>
                            )}
                            {view === 'search' && searchInfo && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }}>
                                            {searchInfo.total > 0 ? `${searchInfo.total} hasil` : 'Tidak ada hasil'} untuk "{searchInfo.q}"
                                            {searchInfo.total > SEARCH_RESULT_LIMIT && (
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    {` (ditampilkan ${SEARCH_RESULT_LIMIT} paling cocok, perjelas kata kuncinya)`}
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Button size="small" startIcon={<CloseIcon />} onClick={closeSearch}>Tutup pencarian</Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                        {SEARCH_TYPES.map((item) => {
                                            const active = searchInfo.type === item.id;
                                            const count = searchInfo.counts[item.id] || 0;
                                            if (!active && item.id !== 'all' && count === 0) return null;
                                            return (
                                                <Chip key={item.id} size="small" label={`${item.label} ${count}`}
                                                    color={active ? 'primary' : 'default'} variant={active ? 'filled' : 'outlined'}
                                                    onClick={() => goToSearch(searchInfo.q, { type: item.id, within: searchInfo.within, replace: true })} />
                                            );
                                        })}
                                        {searchInfo.within ? (
                                            <Chip size="small" color="primary" label={`Hanya di ${searchInfo.within}`}
                                                onDelete={() => goToSearch(searchInfo.q, { type: searchInfo.type, replace: true })} />
                                        ) : lastFolderRef.current ? (
                                            <Chip size="small" variant="outlined" label={`Hanya di ${lastFolderRef.current}`}
                                                onClick={() => goToSearch(searchInfo.q, { type: searchInfo.type, within: lastFolderRef.current, replace: true })} />
                                        ) : null}
                                    </Box>
                                    {searchInfo.total === 0 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Semua kata harus ada di nama file atau nama foldernya. Coba kata yang lebih sedikit, atau hapus filter di atas.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {view === 'files' && path === '' && !searchValue && (
                            <ContinueWatching items={continueItems} onOpen={onOpenContinue} onRemove={onRemoveContinue} />
                        )}

                        <Box sx={{ flex: 1, minHeight: 0, px: { xs: 1, sm: 3 }, pb: 2 }}>
                            <DataGrid
                                sx={{
                                    height: '100%',
                                    width: 1,
                                    '& .MuiDataGrid-cell--editing': {
                                        bgcolor: 'rgb(255,215,115, 0.19)',
                                        color: '#1a3e72',
                                        '& .MuiInputBase-root': {
                                            height: '100%',
                                        },
                                    },
                                    '& .disbox-drop-target': {
                                        outline: '2px dashed #1a90ff',
                                        outlineOffset: '-2px',
                                        bgcolor: 'rgba(26, 144, 255, 0.15)',
                                    },
                                    '& .Mui-error': {
                                        bgcolor: (muiTheme) =>
                                            `rgb(126,10,15, ${muiTheme.palette.mode === 'dark' ? 0 : 0.1})`,
                                        color: (muiTheme) => (muiTheme.palette.mode === 'dark' ? '#ff4343' : '#750f0f'),
                                    },
                                }}
                                style={{ border: "0px" }}
                                rows={rows}
                                columns={buildColumns({ currentAction, trashMode: viewingTrash, isMobile, showLocation, handlers: columnHandlers })}
                                rowHeight={isMobile || showLocation ? 58 : 52}
                                sortModel={view === 'search' ? searchSort : folderSort}
                                onSortModelChange={(model) => (view === 'search' ? setSearchSort(model) : setFolderSort(model))}
                                hideFooter={true}
                                checkboxSelection
                                disableSelectionOnClick
                                showColumnRightBorder={false}
                                selectionModel={selectionModel}
                                onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                                onCellEditCommit={onCellEditCommit}
                                onCellEditStart={() => setEditingName(true)}
                                onCellEditStop={() => setEditingName(false)}
                                onCellDoubleClick={onCellDoubleClick}
                                onCellClick={onCellClick}
                                getRowClassName={(params) => (params.id === dropTargetId ? 'disbox-drop-target' : '')}
                                componentsProps={{
                                    row: {
                                        draggable: !viewingTrash && !isMobile && !editingName,
                                        onDragStart: onRowDragStart,
                                        onDragOver: onRowDragOver,
                                        onDragLeave: onRowDragLeave,
                                        onDrop: onRowDrop,
                                        onDragEnd: onRowDragEnd,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}
export default App;
